import React, { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { useStripe, PaymentRequestButtonElement } from '@stripe/react-stripe-js';
import { setPaymentFormInUse } from '../slices/paymentFormInUse';
import { useFormikContext } from 'formik';
import { processPayment } from '../thunks/processPayment';
import { dollarsToCents } from '../libs/polygon-ordering/src/utils/misc';
import { OrderingConstants, OrderingSelectors } from 'polygon-ordering';
import type { PaymentRequest } from '@stripe/stripe-js';
import { TYRO_CONSTANTS, useTyro } from '../hooks/TyroProvider';
import { RiRefreshLine } from 'react-icons/ri';
import StandardButton from './StandardButton';
import { useTranslation } from 'react-i18next';

const { getCalculatedGiftcardSurcharge, getGatewayType } = OrderingSelectors;
const { PAYMENT_GATEWAY_TYPES } = OrderingConstants;

const { getStripeCurrency } = OrderingSelectors;
const AppleGooglePayButtons = ({
  route,
  containerStyle,
  label,
  paymentStatusPolling,
}: {
  route: string;
  containerStyle: any;
  label: string;
  paymentStatusPolling?: boolean;
}) => {
  const gatewayType = useAppSelector(getGatewayType);
  const [paymentRequest, setPaymentRequest] = useState<null | PaymentRequest>(null);
  const stripeCurrency = useAppSelector(getStripeCurrency);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const formik = useFormikContext<FormikFields>();

  const { values } = formik;

  const surcharge = useAppSelector(state =>
    getCalculatedGiftcardSurcharge(state, Number(values.Amount)),
  );

  // eslint-disable-next-line
  const stripe = gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE ? useStripe() : null;

  useEffect(() => {
    if (gatewayType !== PAYMENT_GATEWAY_TYPES.STRIPE || !stripe) return;
    const pr = stripe.paymentRequest({
      country: 'AU', // TODO: fix to avoid stripe overseas fees
      currency: stripeCurrency,
      total: {
        label: 'Payment Total',
        amount: dollarsToCents(values.Amount!) + Number(surcharge.value),
      },
      requestPayerName: true,
      requestPayerEmail: true,
    });

    pr.canMakePayment().then(async result => {
      if (result?.applePay || result?.googlePay) {
        setPaymentRequest(pr);
      }
    });
  }, [stripe, gatewayType]);

  useEffect(() => {
    if (!paymentRequest) return;
    setPaymentRequest(paymentRequest => {
      if (paymentRequest && values.Amount !== undefined) {
        paymentRequest.update({
          total: {
            label: 'Payment Total',
            amount: dollarsToCents(values.Amount!) + Number(surcharge.value),
          },
        });
      }
      return paymentRequest;
    });
  }, [paymentRequest, values.Amount]);

  useEffect(() => {
    if (!paymentRequest) return;

    paymentRequest.on('cancel', () => {
      dispatch(setPaymentFormInUse(false));
    });

    paymentRequest.on('paymentmethod', ev => {
      dispatch(setPaymentFormInUse(true));

      const data = {
        values: formik.values,
        token: ev.paymentMethod,
        route,
        giftcardSurcharge: surcharge.value,
      };

      dispatch(processPayment(data)).then((response: any) => {
        if (response.meta.requestStatus === 'fulfilled') {
          formik.resetForm();
          ev.complete('success');
        }

        if (response.meta.requestStatus === 'rejected') {
          ev.complete('fail');
        }
      });
    });

    return () => {
      paymentRequest.off('cancel');
      paymentRequest.off('paymentmethod');
    };
  }, [paymentRequest, formik, surcharge.value]);

  // eslint-disable-next-line
  const tyroData = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO ? useTyro() : null;

  const {
    tyroError,
    submittingTyroForm,
    submittingOverlay,
    loading,
    payRequestReady,
    payFormReady,
  } = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && tyroData;

  const showLoadingSpinner = Boolean(
    (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO &&
      (loading || submittingTyroForm || submittingOverlay || paymentStatusPolling)) ||
      (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE && !paymentRequest),
  );

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {(submittingTyroForm || submittingOverlay) && showLoadingSpinner && (
          <div style={{ marginRight: 4 }}>{t('button.submittingPayment')}</div>
        )}
        {showLoadingSpinner && (
          <div style={styles.spinnerContainer}>
            <RiRefreshLine className="spinner" style={styles.loadingSpinner} />
          </div>
        )}
      </div>

      {gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && (
        <>
          <div
            id={TYRO_CONSTANTS.APPLE_GOOGLE_PAY_HTML_ID}
            style={{
              display:
                !loading && !showLoadingSpinner && !tyroError && payRequestReady && payFormReady
                  ? 'block'
                  : 'none',
            }}
          ></div>
        </>
      )}

      {gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE && paymentRequest && !showLoadingSpinner && (
        <PaymentRequestButtonElement options={{ paymentRequest }} />
      )}

      {!showLoadingSpinner && tyroError && (
        <div style={{ marginTop: 20 }}>
          <StandardButton
            disabled={true}
            themeKey="loyaltyButton"
            containerStyle={containerStyle}
            label={label}
          />
        </div>
      )}
    </>
  );
};

export default AppleGooglePayButtons;

const styles: Styles = {
  spinnerContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
  },
  loadingSpinner: {
    fontSize: 24,
  },
};
